/**
 * The full company profile on /insights/company/:id (SALES_INSIGHTS_PLAN.md
 * §5d, M8): every metric we hold for ANY company — coverage, runway, social by
 * platform, readership, the hot-list reasons — plus a "Copy readership report"
 * for a PR firm's "how many people read it?".
 *
 * Honesty rules (consulted salesplanpro-18 and socials-expert, 2026-10-05):
 * page views are exact when the backend says so (every page counted: Bell Wire
 * and Orecast from 28 Sep 2026, other sites from 13 Aug) and a FLOOR otherwise;
 * Google clicks are always a floor (top 100 pages a day); social figures are a
 * one-shot snapshot 12h–4d after each post; LinkedIn reports reach but no clicks.
 */
import { useState } from 'react';
import { Check, ClipboardCopy } from 'lucide-react';
import type { CompanyArticles, SocialByPlatform } from '../../lib/api/insights.js';
import { MASTHEAD_LABEL } from './StoryList.js';

const n = (v: number | null | undefined) => Math.round(v ?? 0).toLocaleString();
const day = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const PLATFORM: Record<string, string> = { linkedin: 'LinkedIn', twitter: 'X' };

function socialLine(p: SocialByPlatform): string {
  const parts = [`${p.posts} post${p.posts === 1 ? '' : 's'}`, `${n(p.impressions)} impressions`];
  if (p.reach) parts.push(`reached ${n(p.reach)} unique members`);
  const eng = (p.reactions ?? 0) + (p.comments ?? 0) + (p.shares ?? 0) + (p.reposts ?? 0);
  if (eng) parts.push(`${n(eng)} reactions/comments/shares`);
  if (p.platform !== 'linkedin' && p.clicks) parts.push(`${n(p.clicks)} link clicks`);
  return parts.join(', ');
}

/** Plain text a salesperson can paste to a PR firm. Never claims LinkedIn clicks;
 *  page views are stated as a minimum. */
export function readershipReport(d: CompanyArticles, windowLabel: string): string {
  const name = d.name ?? d.ticker ?? 'the company';
  const asAt = d.social?.map((p) => p.as_at).filter(Boolean).sort().pop() ?? null;
  const mastheads = (d.coverage?.mastheads ?? []).map((m) => MASTHEAD_LABEL[m] ?? m).join(' and ');
  const lines = [
    `Readership of our coverage of ${name}${d.ticker ? ` (ASX: ${d.ticker})` : ''} — ${windowLabel.toLowerCase()}${asAt ? `, as at ${day(asAt)}` : ''}`,
    '',
    `${d.stories.length} ${d.stories.length === 1 ? 'story' : 'stories'}${mastheads ? ` published on ${mastheads}` : ''}.`,
  ];
  for (const p of d.social ?? []) lines.push(`${PLATFORM[p.platform] ?? p.platform}: ${socialLine(p)}.`);
  if (d.readership?.ai_reads) {
    lines.push(`AI assistants: ${d.readership.ai_reads_estimated ? 'about ' : ''}${n(d.readership.ai_reads)} times an AI assistant (such as ChatGPT, Claude or Perplexity) fetched these articles because someone asked it a question, since 6 Sep 2026.`);
  }
  if (d.readership) {
    lines.push(d.readership.views > 0
      ? `Website: ${d.readership.floor ? 'at least ' : ''}${n(d.readership.views)} human page views recorded on the articles${d.readership.search_clicks ? ` and at least ${n(d.readership.search_clicks)} clicks from Google search` : ''}.`
      : 'Website: page views on these articles were below the level our analytics record individually.');
  }
  lines.push('', 'Articles:');
  for (const s of d.stories) {
    const bits = [day(s.published_at), s.title ?? 'Untitled', s.url ?? ''].filter(Boolean).join(' — ');
    const stats = [s.views ? `${n(s.views)}${s.views_exact ? '' : '+'} views` : '',
      s.ai_reads ? `${s.ai_reads_estimated ? '~' : ''}${n(s.ai_reads)} AI-assisted reads` : '', s.impressions ? `${n(s.impressions)} social impressions` : '']
      .filter(Boolean).join(', ');
    lines.push(`• ${bits}${stats ? ` (${stats})` : ''}`);
  }
  lines.push('', 'Social figures are captured once, 12 hours to 4 days after each post. LinkedIn reports reach, not link clicks.');
  return lines.join('\n');
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-grey-200 px-3 py-2">
      <div className="text-[11px] text-grey-500">{label}</div>
      <div className="text-base font-semibold text-grey-900 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-grey-500">{sub}</div>}
    </div>
  );
}

export function ReadershipReportButton({ d, windowLabel }: { d: CompanyArticles; windowLabel: string }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button"
      onClick={async () => { await navigator.clipboard.writeText(readershipReport(d, windowLabel)); setDone(true); setTimeout(() => setDone(false), 1800); }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-grey-200 bg-surface px-2.5 py-1 text-xs text-grey-700 hover:bg-grey-50">
      {done ? <Check className="h-3.5 w-3.5" /> : <ClipboardCopy className="h-3.5 w-3.5" />}
      {done ? 'Copied' : 'Copy readership report'}
    </button>
  );
}

export function CompanyProfile({ d }: { d: CompanyArticles }) {
  const social = d.social ?? [];
  const impressions = social.reduce((a, p) => a + (p.impressions ?? 0), 0);
  const li = social.find((p) => p.platform === 'linkedin');
  const x = social.find((p) => p.platform === 'twitter');
  const unavailable = social.reduce((a, p) => a + p.not_available + p.pending, 0);
  const asAt = social.map((p) => p.as_at).filter(Boolean).sort().pop() ?? null;
  return (
    <section className="bg-surface rounded-xl border border-grey-200 p-4 space-y-3" aria-label="Company profile">
      {(d.reasons?.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-1">
          {d.reasons!.map((r) => (
            <span key={r.key} className="inline-block rounded-full bg-grey-100 px-2 py-0.5 text-[11px] leading-4 text-grey-700">{r.label}</span>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label="Articles" value={`${d.coverage?.articles_90d ?? 0} in 90 days`} sub={`${d.coverage?.articles_total ?? 0} all time · last ${day(d.coverage?.last_published)}`} />
        <Tile label="Runway" value={d.runway?.quarters == null ? '—' : `${d.runway.quarters.toFixed(1)} qtrs`} sub={d.runway?.period_end ? `to ${day(d.runway.period_end)}` : undefined} />
        <Tile label="Social impressions" value={n(impressions)} sub={asAt ? `as at ${day(asAt)}` : undefined} />
        <Tile label="LinkedIn reach" value={li?.reach ? n(li.reach) : '—'} sub="unique members" />
        <Tile label="X link clicks" value={x?.clicks ? n(x.clicks) : '—'} sub="LinkedIn reports no clicks" />
        <Tile label="Page views" value={d.readership && d.readership.views > 0 ? `${n(d.readership.views)}${d.readership.floor ? '+' : ''}` : '—'}
          sub={d.readership?.floor === false ? 'every page counted' : 'minimum — see note'} />
        <Tile label="AI-assisted reads" value={d.readership?.ai_reads ? `${d.readership.ai_reads_estimated ? '~' : ''}${n(d.readership.ai_reads)}` : '—'}
          sub="AI fetched it for a person, since 6 Sep" />
        <Tile label="Google clicks" value={d.readership && d.readership.search_clicks > 0 ? `${n(d.readership.search_clicks)}+` : '—'} sub="minimum" />
        <Tile label="Mastheads" value={(d.coverage?.mastheads ?? []).map((m) => MASTHEAD_LABEL[m] ?? m).join(', ') || '—'} />
      </div>
      {social.length > 0 && (
        <table className="w-full text-xs">
          <thead><tr className="text-left text-grey-500">
            <th className="py-1 font-medium">Platform</th><th className="py-1 font-medium text-right">Posts</th>
            <th className="py-1 font-medium text-right">Impressions</th><th className="py-1 font-medium text-right">Reach</th>
            <th className="py-1 font-medium text-right">Reactions</th><th className="py-1 font-medium text-right">Comments</th>
            <th className="py-1 font-medium text-right">Shares/reposts</th><th className="py-1 font-medium text-right">Clicks</th>
          </tr></thead>
          <tbody>
            {social.map((p) => (
              <tr key={p.platform} className="border-t border-grey-100 text-grey-800 tabular-nums">
                <td className="py-1">{PLATFORM[p.platform] ?? p.platform}</td>
                <td className="py-1 text-right">{p.posts}</td>
                <td className="py-1 text-right">{n(p.impressions)}</td>
                <td className="py-1 text-right">{p.reach == null ? '—' : n(p.reach)}</td>
                <td className="py-1 text-right">{n(p.reactions)}</td>
                <td className="py-1 text-right">{n(p.comments)}</td>
                <td className="py-1 text-right">{n((p.shares ?? 0) + (p.reposts ?? 0))}</td>
                <td className="py-1 text-right">{p.platform === 'linkedin' ? '—' : n(p.clicks)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-[11px] text-grey-500">
        Social figures are captured once, 12 hours to 4 days after each post{unavailable ? ` (${unavailable} post${unavailable === 1 ? '' : 's'} without figures yet)` : ''}.
        {' '}{d.readership?.note}
      </p>
    </section>
  );
}
